import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, FileText, Sparkles, Gift, Target, Users } from 'lucide-react';

/**
 * BetaNoticeBanner - Compact full-width banner for open beta announcement.
 * Now emphasizes open participation and membership benefits.
 * Mission statement and member ownership are separate collapsible sections.
 */
export default function BetaNoticeBanner() {
  const [betaExpanded, setBetaExpanded] = useState(false);
  const [missionExpanded, setMissionExpanded] = useState(false);
  const [ownershipExpanded, setOwnershipExpanded] = useState(false);

  return (
    <div className="bg-amber-50 border-b border-amber-200" data-testid="beta-notice-banner">
      <div className="container mx-auto px-4 py-2.5">
        {/* Beta Notice Row */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="w-4 h-4 text-amber-600 shrink-0" />
            <p className="text-sm text-amber-900">
              <span className="font-semibold">Open Beta!</span>
              <span className="hidden sm:inline"> — We're now open to everyone. Join us and help shape the future of AADC!</span>
              <span className="sm:hidden"> — Now open to everyone!</span>
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link
              to="/membership"
              className="hidden sm:inline-flex items-center gap-1 text-xs bg-amber-600 text-white px-2 py-1 rounded hover:bg-amber-700 font-medium transition-colors"
              data-testid="beta-membership-link"
            >
              <Gift className="w-3 h-3" /> Beta Discount
            </Link>
            <button
              onClick={() => setBetaExpanded(!betaExpanded)}
              className="flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 font-medium transition-colors"
              data-testid="beta-notice-toggle"
            >
              {betaExpanded ? 'Less' : 'Learn More'}
              {betaExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          </div>
        </div>

        {/* Beta Details (collapsible) */}
        {betaExpanded && (
          <div className="mt-2 pt-2 border-t border-amber-200" data-testid="beta-notice-details">
            <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800 font-medium flex items-center gap-2">
                <Gift className="w-4 h-4" />
                Beta Special: Get 2 years of membership for the price of 1!
              </p>
              <p className="text-xs text-green-700 mt-1">
                Support us during beta and lock in this exclusive deal. 
                <Link to="/membership" className="underline ml-1 font-medium">View membership options →</Link>
              </p>
            </div>

            {/* Beta Tester Choice */}
            <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800 font-medium">Choose Your Role</p>
              <p className="text-xs text-blue-700 mt-1">
                <span className="font-semibold">Be a Beta Tester:</span> Help test features, report bugs, and shape the platform. 
                Beta testers get recognition, early access to new features, and a voice in what we build next.
              </p>
              <p className="text-xs text-blue-700 mt-1">
                <span className="font-semibold">Or just join:</span> Not interested in testing? No problem! 
                Join as a regular member and enjoy the platform — you'll still get the beta pricing discount.
              </p>
              <Link to="/beta-agreement" className="inline-flex items-center gap-1 text-xs text-blue-600 font-medium hover:text-blue-800 mt-1">
                Learn about beta tester benefits →
              </Link>
            </div>

            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-amber-800">
              <li className="flex items-start gap-1.5">
                <span className="text-amber-500 mt-0.5">✓</span>
                Beta is now open — no access code needed
              </li>
              <li className="flex items-start gap-1.5">
                <span className="text-amber-500 mt-0.5">✓</span>
                Your feedback directly shapes what we build
              </li>
              <li className="flex items-start gap-1.5">
                <span className="text-amber-500 mt-0.5">*</span>
                Features may be added, changed, or removed as we improve
              </li>
              <li className="flex items-start gap-1.5">
                <span className="text-amber-500 mt-0.5">*</span>
                Pricing and policies may adjust during beta
              </li>
              <li className="flex items-start gap-1.5">
                <span className="text-amber-500 mt-0.5">✓</span>
                Your data is never sold to third parties
              </li>
              <li className="flex items-start gap-1.5">
                <span className="text-amber-500 mt-0.5">✓</span>
                We're committed to preserving your pet profiles and records
              </li>
            </ul>

            <div className="mt-2 flex items-center justify-between">
              <Link
                to="/beta-agreement"
                className="inline-flex items-center gap-1 text-xs text-amber-700 font-medium hover:text-amber-900"
              >
                <FileText className="w-3 h-3" /> Read Full Beta Agreement
              </Link>
              <Link
                to="/membership"
                className="sm:hidden inline-flex items-center gap-1 text-xs bg-amber-600 text-white px-2 py-1 rounded font-medium"
              >
                <Gift className="w-3 h-3" /> Get Beta Discount
              </Link>
            </div>
          </div>
        )}

        {/* Mission Statement Row (separate collapsible) */}
        <div className={`flex items-center justify-between gap-3 ${betaExpanded || missionExpanded ? 'mt-2 pt-2 border-t border-amber-200' : 'mt-2'}`}>
          <div className="flex items-center gap-2 min-w-0">
            <Target className="w-4 h-4 text-amber-700 shrink-0" />
            <p className="text-sm text-amber-900">
              <span className="font-semibold">Our Purpose</span>
              <span className="hidden sm:inline"> — Building a real-world community of dog people, run by members</span>
            </p>
          </div>
          <button
            onClick={() => setMissionExpanded(!missionExpanded)}
            className="flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 font-medium transition-colors shrink-0"
            data-testid="mission-toggle"
          >
            {missionExpanded ? 'Less' : 'Read More'}
            {missionExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>

        {/* Mission Details (collapsible) */}
        {missionExpanded && (
          <div className="mt-2 p-3 bg-amber-100/50 border border-amber-200 rounded-lg" data-testid="mission-details">
            <p className="text-xs text-amber-900 leading-relaxed">
              <span className="font-semibold">Our Purpose:</span> To enable a new real-world community of 
              "dog people" — run by club members and working collaboratively with other kennel clubs, 
              dog-related organizations, industry participants, and even governments for the benefit of all.
            </p>
            <p className="text-xs text-amber-900 leading-relaxed mt-2">
              When you join as a free or paid member, you will have a voice in how the club is run. 
              We can help fund NGOs, lobby for local dog parks, assist in research, and find local activities.
            </p>
            <div className="mt-2 flex gap-3">
              <Link
                to="/about"
                className="inline-flex items-center gap-1 text-xs text-amber-700 font-medium hover:text-amber-900"
              >
                Learn more about us →
              </Link>
              <Link
                to="/about/bylaws"
                className="inline-flex items-center gap-1 text-xs text-amber-700 font-medium hover:text-amber-900"
              >
                Read our bylaws →
              </Link>
            </div>
          </div>
        )}

        {/* This is Your Club Row (separate collapsible) */}
        <div className={`flex items-center justify-between gap-3 ${missionExpanded || ownershipExpanded ? 'mt-2 pt-2 border-t border-amber-200' : 'mt-2'}`}>
          <div className="flex items-center gap-2 min-w-0">
            <Users className="w-4 h-4 text-amber-700 shrink-0" />
            <p className="text-sm text-amber-900">
              <span className="font-semibold">This is Your Club!</span>
              <span className="hidden sm:inline"> — Members shape governance, programs, and the future</span>
            </p>
          </div>
          <button
            onClick={() => setOwnershipExpanded(!ownershipExpanded)}
            className="flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 font-medium transition-colors shrink-0"
            data-testid="ownership-toggle"
          >
            {ownershipExpanded ? 'Less' : 'Read More'}
            {ownershipExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>

        {/* Ownership Details (collapsible) */}
        {ownershipExpanded && (
          <div className="mt-2 p-3 bg-amber-100/50 border border-amber-200 rounded-lg" data-testid="ownership-details">
            <p className="text-xs text-amber-900 leading-relaxed">
              <span className="font-semibold">Member-Driven Governance:</span> Unlike traditional social platforms 
              where decisions are made in corporate boardrooms, AADC puts members first. Your voice matters in 
              elections, policy discussions, and community direction.
            </p>
            <p className="text-xs text-amber-900 leading-relaxed mt-2">
              <span className="font-semibold">What members influence:</span> Club programs and events, 
              partnerships with rescues and NGOs, community guidelines, feature priorities, and how we 
              allocate resources to benefit dogs and their people.
            </p>
            <p className="text-xs text-amber-900 leading-relaxed mt-2">
              <span className="font-semibold">Transparency:</span> We publish financial reports, hold open 
              elections for club officers, and welcome feedback through our beta program. This is a community 
              built together, not handed down from above.
            </p>
            <div className="mt-2 flex gap-3">
              <Link
                to="/elections"
                className="inline-flex items-center gap-1 text-xs text-amber-700 font-medium hover:text-amber-900"
              >
                View elections →
              </Link>
              <Link
                to="/beta-feedback"
                className="inline-flex items-center gap-1 text-xs text-amber-700 font-medium hover:text-amber-900"
              >
                Share feedback →
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
